<template>
	<main class="app-main">
		<div class="content-wrap">
			<section class="content-section" v-if="investmentsEicAccount">
				<header class="section-header">
					<h2 class="section-title">{{ $t('routes.investments_eic_account.title') }}:
						<span v-if="investmentsEicAccount.name">{{ investmentsEicAccount.name }} -</span>
						{{ investmentsEicAccount.account_id }}</h2>
				</header>
				<article>
					<actions-panel>
						<a class="button style-1" @click.prevent="$router.safeBack">
							<span class="inner">{{ $t('general.back') }}</span>
						</a>
					</actions-panel>
					<chart-panels-group :panels-data="panelsData" :charts-data="chartsData" v-if="portfolios.length" :tabs="true"/>
					<p v-else>{{ $t('investments.no_data') }}</p>
				</article>
			</section>
		</div>
	</main>
</template>

<script>
import investmentsMixin from "@/mixins/investmentsMixin";
import ActionsPanel from "@/components/panels/ActionsPanel";
import ChartPanelsGroup from "@/components/containers/ChartPanelsGroup";
import PanelBoxesContainer from "@/components/containers/PanelBoxesContainer";
import ChartInvestmentInfoGroup from "@/components/containers/ChartInvestmentInfoGroup.vue";

export default {
	name: "InvestmentsEicAccount",
	components: {
		ChartInvestmentInfoGroup,
		ActionsPanel,
		ChartPanelsGroup,
		PanelBoxesContainer
	},
	mixins: [investmentsMixin],
	computed: {
		investmentsEicAccount() {
			return this.$store.state.views.investmentsEicAccount;
		},
		transactions() {
			let bankTransactions = this.investmentsEicAccount.bankTransactions.map(item => {
				return {
					data: item,
					type: 'bank',
					date: item.createdDate
				};
			});

			let tradeTransfers = this.investmentsEicAccount.tradingUnitTransfers.map(item => {
				return {
					data: item,
					type: 'transfer',
					date: item.createdDate
				};
			});

			let transactions = tradeTransfers.concat(bankTransactions);
			transactions.sort((a, b) => {
				return new Date(b.date) - new Date(a.date);
			});

			return transactions.reverse();
		},
		portfolios() {
			let data = [];
			this.investmentsEicAccount.currentPortfolio.forEach(item => {
				if (item.type !== 'Standard' || !item.amount) {
					return;
				}

				data.push(item);
			});

			return data;
		},
		chartsData() {
			let data = [
				this.balanceChart,
				this.portfoliosChart,
			];

			return data;
		},
		balanceChart() {
			let data = this.getBalanceChartOptions();
			data.tabName = this.$t('investments.profit');

			let series = [
				{
					name: this.$t('investments.account_balance'),
					data: [],
				},
				{
					name: this.$t('investments.transactions'),
					data: [],
				}
			];

			let currentDate = new Date().toISOString().slice(0, 10);

			this.investmentsEicAccount.history.sort((a, b) => {
				return new Date(a.date) - new Date(b.date);
			});

			this.investmentsEicAccount.history.forEach(item => {
				series[0].data.push({
					x: item.date,
					y: item.balance,
				});
			});

			series[0].data.push({
				x: currentDate,
				y: this.investmentsEicAccount.balance,
			});

			let deposit = 0;

			let transactions = [];
			this.transactions.forEach(item => {
				let amount;
				if (item.type === 'bank') {
					amount = parseFloat(item.data.refAmount.volume);
					if (item.data.creditDebet === 'Debet') {
						amount = amount * -1;
					}
				} else {
					amount = parseFloat(item.data.amount);
					let note = item.data.note;
					note = note.toLowerCase();
					if (note.includes('prevod')) {
						if (item.data.status === 'Executed' || item.data.status === 'Booked') {
							amount = item.data.crAccRefAmount.volume;
							if (item.data.crAccount.accId !== this.investmentsEicAccount.account_id) {
								amount = amount * -1;
							}
						}
					}
				}

				transactions.push({
					amount: amount,
					date: item.date,
					type: item.type
				});
			});
			transactions = transactions.sort((a, b) => {
				return new Date(a.date) - new Date(b.date);
			});

			transactions.forEach(item => {
				deposit += item.amount;
				series[1].data.push({
					x: item.date,
					y: deposit.toFixed(2),
				});
			});

			series[1].data.push({
				x: currentDate,
				y: deposit.toFixed(2),
			});

			data.series = series;
			data.totalBalance = this.investmentsEicAccount.balance;
			data.totalDeposits = this.investmentsEicAccount.deposit;
			data.totalWithdrawals = this.investmentsEicAccount.withdrawal;
			data.totalFees = this.investmentsEicAccount.fee;
			data.totalReturn = this.investmentsEicAccount.return;

			return data;
		},
		portfoliosChart() {
			let data = this.getPortfoliosChartOptions();
			data.tabName = this.$t('investments.allocation');

			let series = [];
			let labels = [];
			this.portfolios.forEach(item => {
				series.push(item.refTotalAmount);
				labels.push(item.ticker.ticker);
			});

			data.chartOptions.labels = labels;
			data.series = series;

			return data;
		},
		panelsData() {
			let data = [];
			this.investmentsEicAccount.currentPortfolio.forEach(item => {
				if (item.type !== 'Standard' || !item.amount) {
					return;
				}

				let currency = this.getCurrencySymbol(item.currency.ticker);

				data.push({
					id: item.id,
					category: this.$t('investments.portfolio'),
					name: item.ticker.ticker,
					description: this.$t('investments.portfolio_balance'),
					price: item.totalAmount,
					returnPercentage: item.totalReturnInPercent,
					type: 'portfolio',
					portfolioType: 'eic',
					currency: currency,
					detail: !!item.ticker.isin,
				});
			});

			return data;
		}
	},
}
</script>

<style scoped>

</style>